import React, { useState, useEffect, useMemo } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import DonutChart from '../../components/Charts/DonutChart'
import BarChart from '../../components/Charts/BarChart'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import LineChart from '../../components/Charts/LineChart'
import GaugeChart from '../../components/Charts/GaugeChart'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { aggregateMultiSelect } from '../../utils/aggregator'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToCSV } from '../../utils/exportUtils'
import { expandMonthName, parseMonthToValue } from '../../utils/dataUtils'
import './StatusAtGlance.css'
import html2canvas  from "html2canvas"

const BCBS_Y2_START_YYYYMM = parseMonthToValue('Sep 2025')

const BCBS_STRUCTURE_API_KEY_TO_SHORT: Record<string, string> = {
    'Coordination - Engaging Community Providers': 'Coordination',
    'Community Partnerships': 'Partnerships',
    'Doula-Friendly Culture': 'Doulas',
    'Data Review Sharing': 'Data Review',
}

const BCBS_STRUCTURE_MEASURE_LABEL_ORDER = [
    'Coordination',
    'Partnerships',
    'Doulas',
    'Data Review',
] as const

function structureMeasureApiToShortLabel(apiMeasureName: string): string {
    const t = apiMeasureName.trim()
    return BCBS_STRUCTURE_API_KEY_TO_SHORT[t] ?? t
}

function sortStructureMeasureShortLabels(shortLabels: string[]): string[] {
    const order = BCBS_STRUCTURE_MEASURE_LABEL_ORDER as readonly string[]
    return [...new Set(shortLabels)].sort((a, b) => {
        const ia = order.indexOf(a)
        const ib = order.indexOf(b)
        if (ia === -1 && ib === -1) return a.localeCompare(b)
        if (ia === -1) return 1
        if (ib === -1) return -1
        return ia - ib
    })
}

const StatusAtGlance: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data: engagementData, loading: engagementLoading, initialLoading: engagementInitialLoading } = useHospitalData('/api/em_criteria_status_y2', selectedHospitalIds)
    const { data: structureData, loading: structureLoading, initialLoading: structureInitialLoading } = useHospitalData('/api/sm_status_count', selectedHospitalIds)
    const { data: educationData, loading: educationLoading, initialLoading: educationInitialLoading } = useHospitalData('/api/education_measures', selectedHospitalIds)
    const { data: premData, loading: premLoading, initialLoading: premInitialLoading } = useHospitalData('/api/prem', selectedHospitalIds)
    const { data: outcomeInsuranceData, loading: outcomeInsuranceLoading, initialLoading: outcomeInsuranceInitialLoading } = useHospitalData('/api/outcome', selectedHospitalIds)
    const { data: outcomeRaceEthnicityData, loading: outcomeRaceEthnicityLoading, initialLoading: outcomeRaceEthnicityInitialLoading } = useHospitalData('/api/outcome_race_ethnicity', selectedHospitalIds)
    const { data: pmhClinicalData, loading: pmhClinicalLoading, initialLoading: pmhClinicalInitialLoading } = useHospitalData('/api/pmh_clinical_education', selectedHospitalIds)

    const [engagementSeries, setEngagementSeries] = useState<number[]>([])
    const [engagementLabels, setEngagementLabels] = useState<string[]>([])
    const [engagementMeasures, setEngagementMeasures] = useState<string[][]>([])
    const [engagementBarSeries, setEngagementBarSeries] = useState<{ name: string; data: number[] }[]>([])
    const [engagementBarCategories, setEngagementBarCategories] = useState<string[]>([])

    const [structureSeries, setStructureSeries] = useState<number[]>([])
    const [structureLabels, setStructureLabels] = useState<string[]>([])
    const [structureMeasures, setStructureMeasures] = useState<string[]>([])

    const [educationSeries, setEducationSeries] = useState<{ name: string; data: number[] }[]>([])
    const [educationCategories, setEducationCategories] = useState<string[]>([])

    const [premSeries, setPremSeries] = useState<{ name: string; data: (number | null)[] }[]>([])
    const [premCategories, setPremCategories] = useState<string[]>([])

    const [outcomeGauges, setOutcomeGauges] = useState<{ cesarean: number; sdoh: number }>({ cesarean: 0, sdoh: 0 })
    const [outcomeDemographicsSeries, setOutcomeDemographicsSeries] = useState<{ name: string; data: number[] }[]>([])
    const [outcomeDemographicsCategories, setOutcomeDemographicsCategories] = useState<string[]>([])

    const [pmhSeries, setPmhSeries] = useState<{ name: string; data: number[] }[]>([])
    const [pmhCategories, setPmhCategories] = useState<string[]>([])

    const reportingMonthValue = useMemo(() => {
        const now = new Date()
        const m = now.getMonth()
        const y = now.getFullYear()
        return m === 0 ? (y - 1) * 100 + 12 : y * 100 + m
    }, [])

    const engagementContainerRef = React.useRef<HTMLDivElement>(null)
    const engagementBarChartRef = React.useRef<any>(null)
    const structureContainerRef = React.useRef<HTMLDivElement>(null)
    const educationContainerRef = React.useRef<HTMLDivElement>(null)
    const engagementMeasuresRef= React.useRef<any>(null)
    const structureMeasuresRef =React.useRef<any>(null)
    const educationChartRef = React.useRef<any>(null)
    const pmhContainerRef = React.useRef<HTMLDivElement>(null)
    const pmhChartRef = React.useRef<any>(null)
    const premContainerRef = React.useRef<HTMLDivElement>(null)
    const premChartRef = React.useRef<any>(null)
    const outcomeContainerRef = React.useRef<HTMLDivElement>(null)
    const gauge1ContainerRef = React.useRef<HTMLDivElement>(null)
    const gauge2ContainerRef = React.useRef<HTMLDivElement>(null)
    const demographicsContainerRef = React.useRef<HTMLDivElement>(null)

    const createExportElement = (
        ref: React.RefObject<HTMLDivElement>,
        options?: { sectionTitle?: string; chartTitle?: string }
    ) => {
        if (!ref.current) return null;

        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.left = '-10000px';
        wrapper.style.top = '0';
        const exportWidth = Math.max(
            ref.current.offsetWidth,
            ref.current.scrollWidth,
            1024
        );
        wrapper.style.width = `${exportWidth}px`;
        wrapper.style.background = '#ffffff';
        wrapper.style.padding = '24px';
        wrapper.style.boxSizing = 'border-box';
        wrapper.style.fontFamily = 'Helvetica, Arial, sans-serif';

        if (options?.sectionTitle) {
            const sectionHeading = document.createElement('div');
            sectionHeading.textContent = options.sectionTitle;
            sectionHeading.style.fontSize = '24px';
            sectionHeading.style.fontWeight = '700';
            sectionHeading.style.color = '#1a2a44';
            sectionHeading.style.marginBottom = '8px';
            wrapper.appendChild(sectionHeading);
        }

        if (options?.chartTitle) {
            const chartHeading = document.createElement('div');
            chartHeading.textContent = options.chartTitle;
            chartHeading.style.fontSize = '16px';
            chartHeading.style.fontWeight = '600';
            chartHeading.style.color = '#333333';
            chartHeading.style.lineHeight = '1.4';
            chartHeading.style.marginBottom = '16px';
            wrapper.appendChild(chartHeading);
        }

        const clonedChart = ref.current.cloneNode(true) as HTMLDivElement;
        clonedChart.style.width = '100%';
        clonedChart.querySelectorAll('.export-ignore, .export-menu, .export-dropdown, .export-ignore2').forEach((el) => el.remove());

        const pageContext = document.createElement('div');
        pageContext.className = 'bcbs-status-at-glance-page';
        pageContext.style.width = '100%';
        pageContext.style.background = '#ffffff';
        pageContext.appendChild(clonedChart);

        const originalCanvases = ref.current.querySelectorAll('canvas');
        const clonedCanvases = clonedChart.querySelectorAll('canvas');
        clonedCanvases.forEach((canvas, index) => {
            const originalCanvas = originalCanvases[index] as HTMLCanvasElement | undefined;
            const clonedCanvas = canvas as HTMLCanvasElement;
            if (!originalCanvas) return;

            clonedCanvas.width = originalCanvas.width;
            clonedCanvas.height = originalCanvas.height;
            clonedCanvas.style.width = originalCanvas.style.width;
            clonedCanvas.style.height = originalCanvas.style.height;

            const context = clonedCanvas.getContext('2d');
            if (context) {
                context.drawImage(originalCanvas, 0, 0);
            }
        });

        wrapper.appendChild(pageContext);
        document.body.appendChild(wrapper);

        return wrapper;
    };

    const handleDownloadPNG = async (
        ref: React.RefObject<HTMLDivElement>,
        filename: string,
        options?: { sectionTitle?: string; chartTitle?: string }
    ) => {
        const exportElement = createExportElement(ref, options);
        if (!exportElement) return;

        try {
            const canvas = await html2canvas(exportElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            } as any);

            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } finally {
            exportElement.remove();
        }
    };

    const handleDownloadSVG = async (
        ref: React.RefObject<HTMLDivElement>,
        filename: string,
        options?: { sectionTitle?: string; chartTitle?: string }
    ) => {
        const exportElement = createExportElement(ref, options);
        if (!exportElement) return;

        try {
            const canvas = await html2canvas(exportElement, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            } as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="white"/>
  <image x="0" y="0" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        } finally {
            exportElement.remove();
        }
    };

    const handleDownloadEngagementCSV = () => {
        exportToCSV({
            headers: ['Status', 'Percent', 'Measures'],
            rows: engagementLabels.map((label, i) => [
                label,
                engagementSeries[i] || 0,
                engagementMeasures[i]?.join('; ') || ''
            ]),
            filename: 'BCBS_Engagement_Measures'
        });
    };

    const handleDownloadStructureCSV = () => {
        const total = structureSeries.reduce((sum, n) => sum + (Number(n) || 0), 0)
        exportToCSV({
            headers: ['Status', 'Percent', 'Measures'],
            rows: structureLabels.map((label, i) => {
                const count = Number(structureSeries[i]) || 0
                const pctRounded = total > 0 ? Math.round((count / total) * 100) : 0
                return [label, `${pctRounded}%`, structureMeasures[i] || '']
            }),
            filename: 'BCBS_Structure_Measures'
        });
    };

    const handleDownloadGaugeCSV = (label: string, value: number, goal: number) => {
        const status = value <= goal ? 'Met Goal' : 'Above Goal';
        exportToCSV({
            headers: ['Measure', 'Percentage', 'Goal', 'Status'],
            rows: [[label, `${value.toFixed(1)}%`, `${goal}%`, status]],
            filename: label.replace(/ /g, '_')
        });
    };

    const handleDownloadOutcomeDemographicsCSV = () => {
        exportToCSV({
            headers: ['Stratified Group', ...outcomeDemographicsSeries.map((item) => item.name), 'Goal'],
            rows: outcomeDemographicsCategories.map((category, index) => [
                category,
                ...outcomeDemographicsSeries.map((item) => `${(item.data[index] ?? 0).toFixed(1)}%`),
                '25%'
            ]),
            filename: 'Outcome_Measures_Demographics'
        });
    };

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) {
            menu.classList.remove('show');
            menu.style.display = '';
        }
    };

    useEffect(() => {
        if (!engagementData) {
            setEngagementSeries([])
            setEngagementLabels([])
            setEngagementMeasures([])
            setEngagementBarSeries([])
            setEngagementBarCategories([])
            return
        }
        const rows = engagementData.response?.result?.rows || []
        const columns = engagementData.response?.result?.column_order || []
        if (rows.length === 0) {
            setEngagementSeries([])
            setEngagementLabels([])
            setEngagementMeasures([])
            setEngagementBarSeries([])
            setEngagementBarCategories([])
            return
        }

        const monthIdx = columns.indexOf('month')
        const metPctIdx = columns.indexOf('met_percent')
        const notMetPctIdx = columns.indexOf('not_met_percent')
        const metMeasuresIdx = columns.indexOf('met_measures')
        const notMetMeasuresIdx = columns.indexOf('not_met_measures')

        const baselineRows: any[] = []
        const latestRows: any[] = []

        rows.forEach((row: any) => {
            const month = monthIdx !== -1 ? row[monthIdx] : ''
            if (month === 'Baseline') {
                baselineRows.push(row)
            } else {
                latestRows.push(row)
            }
        })

        const aggregateRows = (groupRows: any[]) => {
            let met = 0
            let notMet = 0
            let measuresMetSet = new Set<string>()
            let measuresNotMetSet = new Set<string>()
            let month = ''

            groupRows.forEach((row: any) => {
                met += parseFloat(row[metPctIdx !== -1 ? metPctIdx : 6]) || 0
                notMet += parseFloat(row[notMetPctIdx !== -1 ? notMetPctIdx : 7]) || 0

                if (monthIdx !== -1 && row[monthIdx]) {
                    month = row[monthIdx]
                }

                const metMeasStr = row[metMeasuresIdx !== -1 ? metMeasuresIdx : 8]
                const notMetMeasStr = row[notMetMeasuresIdx !== -1 ? notMetMeasuresIdx : 9]

                const mMet = metMeasStr ? metMeasStr.split(', ') : []
                const mNotMet = notMetMeasStr ? notMetMeasStr.split(', ') : []

                mMet.forEach((m: string) => { if (m) measuresMetSet.add(m) })
                mNotMet.forEach((m: string) => { if (m) measuresNotMetSet.add(m) })
            })

            if (groupRows.length > 1) {
                met = Math.round(met / groupRows.length)
                notMet = Math.round(notMet / groupRows.length)
            }

            return { met, notMet, measuresMetSet, measuresNotMetSet, month }
        }

        const latest = aggregateRows(latestRows.length > 0 ? latestRows : rows)
        const baseline = aggregateRows(baselineRows)

        setEngagementSeries([latest.met, latest.notMet])
        setEngagementLabels(['Met', 'Not Met'])

        setEngagementMeasures([
            Array.from(latest.measuresMetSet),
            Array.from(latest.measuresNotMetSet)
        ])

        const reportingMonth = latest.month ? expandMonthName(latest.month) : 'Current'
        setEngagementBarCategories(['Baseline', reportingMonth])
        setEngagementBarSeries([
            { name: 'Met', data: [baseline.met, latest.met] },
            { name: 'Not Met', data: [baseline.notMet, latest.notMet] },
        ])

    }, [engagementData, isMultiSelect])

    useEffect(() => {
        if (!structureData) {
            setStructureSeries([])
            setStructureLabels([])
            setStructureMeasures([])
            return
        }
        const rows = structureData.response?.result?.rows || []
        if (rows.length === 0) {
            setStructureSeries([])
            setStructureLabels([])
            setStructureMeasures([])
            return
        }

        const columns: string[] = structureData.response?.result?.column_order || []
        const colIdx = (candidates: string[], fallback: number) => {
            for (const name of candidates) {
                const i = columns.indexOf(name)
                if (i >= 0) return i
            }
            return fallback
        }
        const hospitalIdx = colIdx(['HospitalId', 'hospital_id'], 0)
        const measureIdx = colIdx(
            ['Measure', 'Measure Name', 'measure_name', 'Structure Measure'],
            3
        )
        const statusIdx = colIdx(
            ['Quality Excellence', 'quality_excellence', 'QualityExcellence', 'Status'],
            4
        )

        const LABEL_HAVENT = "Haven't started"
        const LABEL_WORKING = 'Working on it'
        const LABEL_INPLACE = 'In place'
        const ORDER = [LABEL_HAVENT, LABEL_WORKING, LABEL_INPLACE] as const

        const bucketForStatus = (raw: unknown): (typeof ORDER)[number] | null => {
            const s = String(raw ?? '').trim().toLowerCase()
            if (s === 'red' || s === '0') return LABEL_HAVENT
            if (s === 'yellow' || s === '1') return LABEL_WORKING
            if (s === 'green' || s === '2') return LABEL_INPLACE
            return null
        }

        const hospitalSet = new Set(selectedHospitalIds.map((id) => String(id).trim()))

        const labelMap = new Map<string, { count: number; measures: Set<string> }>()
        ORDER.forEach((k) => labelMap.set(k, { count: 0, measures: new Set() }))

        rows.forEach((row: any[]) => {
            if (!row || !row.length) return
            const hid = row[hospitalIdx]
            if (hospitalSet.size > 0 && !hospitalSet.has(String(hid ?? '').trim())) return

            const bucket = bucketForStatus(row[statusIdx])
            if (!bucket) return

            const measure = String(row[measureIdx] ?? '').trim()
            const entry = labelMap.get(bucket)!
            entry.count += 1
            if (measure) entry.measures.add(measure)
        })

        setStructureLabels([...ORDER])
        setStructureSeries(ORDER.map((k) => labelMap.get(k)!.count))
        setStructureMeasures(
            ORDER.map((k) => {
                const short = Array.from(labelMap.get(k)!.measures).map((m) =>
                    structureMeasureApiToShortLabel(m)
                )
                return sortStructureMeasureShortLabels(short).join(', ')
            })
        )
    }, [structureData, isMultiSelect, selectedHospitalIds])

    useEffect(() => {
        if (!educationData) {
            setEducationSeries([])
            setEducationCategories([])
            return
        }
        const rows = educationData.response?.result?.rows || []
        if (rows.length === 0 || selectedHospitalIds.length === 0) {
            setEducationSeries([])
            setEducationCategories([])
            return
        }

        const columns = educationData.response?.result?.column_order || []
        const getIdx = (name: string) => columns.indexOf(name)
        const hospitalIdx = getIdx('HospitalId')
        const monthIdx = getIdx('Month')
        const yearlyProvIdx = getIdx('Yearly continued education providers')
        const yearlyNurseIdx = getIdx('Yearly continued education nurses')
        const yearlyOtherIdx = getIdx('Yearly continued education other')
        
        const yIdx = [yearlyProvIdx, yearlyNurseIdx, yearlyOtherIdx]
        for (let i = 0; i < 3; i++) {
            if (yIdx[i] === -1) yIdx[i] = 3 + i
        }

        const cell = (row: any[] | undefined, col: number) => {
            if (!row || col < 0) return 0
            const v = parseFloat(String(row[col]))
            return Number.isFinite(v) ? v : 0
        }

        const categories = ['Providers', 'Nurses', 'Other Staff']
        setEducationCategories(categories)

        if (hospitalIdx === -1 || monthIdx === -1) {
            setEducationSeries([])
            return
        }

        if (isMultiSelect) {
            const availableMonthValues = new Set<number>()
            rows.forEach((row: any[]) => {
                const v = parseMonthToValue(String(row[monthIdx] ?? ''))
                if (v > 0) availableMonthValues.add(v)
            })
            const effectiveMonthValue = availableMonthValues.has(reportingMonthValue)
                ? reportingMonthValue
                : Math.max(0, ...Array.from(availableMonthValues))

            if (effectiveMonthValue === 0) {
                setEducationSeries([])
                return
            }

            const rowAtCohort = new Map<string, any[]>()
            rows.forEach((row: any[]) => {
                const m = String(row[monthIdx] ?? '')
                if (parseMonthToValue(m) !== effectiveMonthValue) return
                const hid = String(row[hospitalIdx] ?? '').trim()
                if (!hid) return
                rowAtCohort.set(hid, row)
            })

            const siteCount = selectedHospitalIds.length
            const yearlyTotals = [0, 0, 0]
            selectedHospitalIds.forEach((hidRaw) => {
                const hid = String(hidRaw ?? '').trim()
                const row = rowAtCohort.get(hid)
                for (let i = 0; i < 3; i++) {
                    yearlyTotals[i] += cell(row, yIdx[i])
                }
            })

            const yearly = yearlyTotals.map((t) => (siteCount > 0 ? t / siteCount : 0))

            const allZero = yearly.every((v) => v === 0)
            if (allZero) {
                setEducationSeries([])
                return
            }

            setEducationSeries([
                { name: 'Yearly Continued Education', data: yearly },
            ])
        } else {
            const onlyHid = String(selectedHospitalIds[0] ?? '').trim()
            const forHosp = rows.filter((row: any[]) => String(row[hospitalIdx] ?? '').trim() === onlyHid)
            if (forHosp.length === 0) {
                setEducationSeries([])
                return
            }

            let matchingRow: any[] | undefined = reportingMonthValue > 0
                ? forHosp.find((row: any[]) => parseMonthToValue(String(row[monthIdx] ?? '')) === reportingMonthValue)
                : undefined

            if (!matchingRow) {
                let bestKey = -Infinity
                forHosp.forEach((row: any[]) => {
                    const k = parseMonthToValue(String(row[monthIdx] ?? ''))
                    if (k > bestKey) {
                        bestKey = k
                        matchingRow = row
                    }
                })
            }

            if (!matchingRow) {
                setEducationSeries([])
                return
            }

            const r = matchingRow
            const yearly = [0, 1, 2].map((i) => cell(r, yIdx[i]))
            const allZero = yearly.every((v) => v === 0)
            if (allZero) {
                setEducationSeries([])
                return
            }

            setEducationSeries([
                { name: 'Yearly Continued Education', data: yearly },
            ])
        }
    }, [educationData, isMultiSelect, selectedHospitalIds, reportingMonthValue])

    useEffect(() => {
        if (!premData) {
            setPremSeries([])
            setPremCategories([])
            return
        }
        const rows = premData.response?.result?.rows || []
        if (rows.length === 0) {
            setPremSeries([])
            setPremCategories([])
            return
        }

        const columns: string[] = premData.response?.result?.column_order || []
        // The /api/prem (PREM_Completion_Response_All) view uses lowercase column names
        // (month/percent/report_type); the legacy view used Month/Percent/M-Q-6M-Y Toggle.
        // Accept both casings so the tile survives a view swap (see status PREM fallback note).
        const findColIdx = (names: string[], fallback: number) => {
            for (const n of names) { const i = columns.indexOf(n); if (i !== -1) return i }
            return fallback
        }
        const monthColIdx = findColIdx(['month', 'Month'], 2)
        const percentColIdx = findColIdx(['percent', 'Percent'], 5)
        const toggleColIdx = findColIdx(['report_type', 'M/Q/6M/Y Toggle'], 6)

        const isMonthlyRow = (row: any[]) => String(row[toggleColIdx] ?? '').trim() === 'Monthly'
        const isFromSep2025Onwards = (monthStr: string) => parseMonthToValue(monthStr) >= BCBS_Y2_START_YYYYMM
        const sortByYearMonth = (a: string, b: string) => parseMonthToValue(a) - parseMonthToValue(b)

        const monthlyRows = rows.filter(isMonthlyRow)

        if (monthlyRows.length === 0) {
            setPremSeries([])
            setPremCategories([])
            return
        }

        if (isMultiSelect) {
            try {
                // aggregateMultiSelect resolves columns by the capitalized names
                // HospitalId/Month/Percent; relabel the lowercase view columns so it matches
                // (rows are positional, so only the labels change).
                const normalizedColumns = columns.map(c =>
                    c === 'hospital_id' ? 'HospitalId' : c === 'month' ? 'Month' : c === 'percent' ? 'Percent' : c)
                const premDataMonthly = {
                    ...premData,
                    response: {
                        ...premData.response,
                        result: { ...premData.response.result, column_order: normalizedColumns, rows: monthlyRows }
                    }
                }
                const totalPercentAgg = aggregateMultiSelect(premDataMonthly, 'average', 'Percent', 'actual_month')

                const chartData = totalPercentAgg
                    .filter(item => isFromSep2025Onwards(item.month))
                    .sort((a, b) => sortByYearMonth(a.month, b.month))

                setPremCategories(chartData.map(item => item.month))
                setPremSeries([{
                    name: 'Total Deliverables %',
                    data: chartData.map(item =>
                        typeof item.value === 'number' && !isNaN(item.value)
                            ? Math.round(item.value * 10) / 10
                            : item.value
                    )
                }])
            } catch (error) {
                console.error('Error aggregating PREM data:', error)
                setPremSeries([])
                setPremCategories([])
            }
        } else {
            const filteredRows = monthlyRows
                .filter((row: any) => isFromSep2025Onwards(String(row[monthColIdx] ?? '')))
                .sort((a: any, b: any) => sortByYearMonth(String(a[monthColIdx]), String(b[monthColIdx])))

            setPremCategories(filteredRows.map((row: any) => String(row[monthColIdx])))
            setPremSeries([{
                name: 'Total Deliverables %',
                data: filteredRows.map((row: any) => {
                    const val = row[percentColIdx]
                    if (val === '' || val === null || isNaN(parseFloat(val))) return null
                    return Math.round(parseFloat(val) * 10) / 10
                })
            }])
        }
    }, [premData, isMultiSelect])

    useEffect(() => {
        if (!outcomeInsuranceData) {
            setOutcomeGauges({ cesarean: 0, sdoh: 0 })
            return
        }
        const outcomeRows = outcomeInsuranceData.response?.result?.rows || []
        if (outcomeRows.length === 0) {
            setOutcomeGauges({ cesarean: 0, sdoh: 0 })
            return
        }

        const outcomeColumns = outcomeInsuranceData.response?.result?.column_order || []
        const getOutcomeColumnIndex = (name: string) => outcomeColumns.indexOf(name)
        const outcomeReportTypeIndex = getOutcomeColumnIndex('report_type') !== -1 ? getOutcomeColumnIndex('report_type') : 14
        const outcomePeriodIndex = getOutcomeColumnIndex('Month') !== -1 ? getOutcomeColumnIndex('Month') : 1
        const outcomeCesareanIndex = getOutcomeColumnIndex('Cesarean_Rate') !== -1 ? getOutcomeColumnIndex('Cesarean_Rate') : 9
        const outcomeSdohIndex = getOutcomeColumnIndex('SDOH_Linkage') !== -1 ? getOutcomeColumnIndex('SDOH_Linkage') :
            (getOutcomeColumnIndex('Sdoh_Linkage') !== -1 ? getOutcomeColumnIndex('Sdoh_Linkage') : 10)

        const exactMonthlyOutcomeRows = outcomeReportTypeIndex === -1
            ? outcomeRows
            : outcomeRows.filter((row: any[]) => String(row[outcomeReportTypeIndex] ?? '').trim() === 'Monthly')

        const normalizedMonthlyOutcomeRows = outcomeReportTypeIndex === -1
            ? outcomeRows
            : outcomeRows.filter((row: any[]) => String(row[outcomeReportTypeIndex] ?? '').trim().toLowerCase() === 'monthly')

        const monthlyOutcomeRows =
            exactMonthlyOutcomeRows.length > 0
                ? exactMonthlyOutcomeRows
                : normalizedMonthlyOutcomeRows.length > 0
                    ? normalizedMonthlyOutcomeRows
                    : outcomeRows

        let latestOutcomeRows = reportingMonthValue > 0
            ? monthlyOutcomeRows.filter((row: any[]) =>
                parseMonthToValue(String(row[outcomePeriodIndex] ?? '')) === reportingMonthValue)
            : []

        if (latestOutcomeRows.length === 0) {
            const latestOutcomeMonthValue = monthlyOutcomeRows.reduce((latestMonth: number, row: any[]) => {
                const currentMonth = parseMonthToValue(String(row[outcomePeriodIndex] ?? ''))
                return currentMonth > latestMonth ? currentMonth : latestMonth
            }, 0)
            latestOutcomeRows = latestOutcomeMonthValue > 0
                ? monthlyOutcomeRows.filter((row: any[]) =>
                    parseMonthToValue(String(row[outcomePeriodIndex] ?? '')) === latestOutcomeMonthValue)
                : monthlyOutcomeRows
        }

        const cesareanValues = latestOutcomeRows
            .map((row: any[]) => parseFloat(row[outcomeCesareanIndex]))
            .filter((value: number) => !Number.isNaN(value))

        const sdohValues = latestOutcomeRows
            .map((row: any[]) => parseFloat(row[outcomeSdohIndex]))
            .filter((value: number) => !Number.isNaN(value))

        // Multi-select: mean-of-means dividing by the total rows in the latest month
        // (incl. rows with empty rate) to match the Outcome Measures page aggregation.
        const cesarean = cesareanValues.length > 0
            ? (isMultiSelect
                ? cesareanValues.reduce((sum: number, value: number) => sum + value, 0) / latestOutcomeRows.length
                : cesareanValues[0])
            : 0
        const sdoh = sdohValues.length > 0
            ? (isMultiSelect
                ? sdohValues.reduce((sum: number, value: number) => sum + value, 0) / latestOutcomeRows.length
                : sdohValues[0])
            : 0

        setOutcomeGauges({ cesarean, sdoh })
    }, [outcomeInsuranceData, isMultiSelect, reportingMonthValue])

    useEffect(() => {
        if (!outcomeInsuranceData || !outcomeRaceEthnicityData) {
            setOutcomeDemographicsSeries([])
            setOutcomeDemographicsCategories([])
            return
        }

        const outcomeRows = outcomeInsuranceData.response?.result?.rows || []
        const raceRows = outcomeRaceEthnicityData.response?.result?.rows || []

        if (outcomeRows.length === 0 || raceRows.length === 0) {
            setOutcomeDemographicsSeries([])
            setOutcomeDemographicsCategories([])
            return
        }

        let publicInsurance = 0
        let privateInsurance = 0

        const outcomeColumns = outcomeInsuranceData.response?.result?.column_order || []
        const getOutcomeColumnIndex = (name: string) => outcomeColumns.indexOf(name)
        const outcomeReportTypeIndex = getOutcomeColumnIndex('report_type') !== -1 ? getOutcomeColumnIndex('report_type') : 14
        const outcomePeriodIndex = getOutcomeColumnIndex('Month') !== -1 ? getOutcomeColumnIndex('Month') : 1
        const outcomePublicIndex = getOutcomeColumnIndex('Public') !== -1 ? getOutcomeColumnIndex('Public') : 8
        const outcomePrivateIndex = getOutcomeColumnIndex('Private') !== -1 ? getOutcomeColumnIndex('Private') : 7

        if (!outcomeRows.some((row: any[]) => row[outcomePeriodIndex] !== undefined)) {
            setOutcomeDemographicsSeries([])
            setOutcomeDemographicsCategories([])
            return
        }

        const exactMonthlyOutcomeRows = outcomeReportTypeIndex === -1
            ? outcomeRows
            : outcomeRows.filter((row: any[]) => String(row[outcomeReportTypeIndex] ?? '').trim() === 'Monthly')

        const normalizedMonthlyOutcomeRows = outcomeReportTypeIndex === -1
            ? outcomeRows
            : outcomeRows.filter((row: any[]) => String(row[outcomeReportTypeIndex] ?? '').trim().toLowerCase() === 'monthly')

        const monthlyOutcomeRows =
            exactMonthlyOutcomeRows.length > 0
                ? exactMonthlyOutcomeRows
                : normalizedMonthlyOutcomeRows.length > 0
                    ? normalizedMonthlyOutcomeRows
                    : outcomeRows

        let latestOutcomeRows = reportingMonthValue > 0
            ? monthlyOutcomeRows.filter((row: any[]) =>
                parseMonthToValue(String(row[outcomePeriodIndex] ?? '')) === reportingMonthValue)
            : []

        if (latestOutcomeRows.length === 0) {
            const latestOutcomeMonthValue = monthlyOutcomeRows.reduce((latestMonth: number, row: any[]) => {
                const currentMonth = parseMonthToValue(String(row[outcomePeriodIndex] ?? ''))
                return currentMonth > latestMonth ? currentMonth : latestMonth
            }, 0)
            latestOutcomeRows = latestOutcomeMonthValue > 0
                ? monthlyOutcomeRows.filter((row: any[]) =>
                    parseMonthToValue(String(row[outcomePeriodIndex] ?? '')) === latestOutcomeMonthValue)
                : monthlyOutcomeRows
        }

        // Divide by the total rows in the latest month (incl. rows with empty values)
        // to match the Outcome Measures page's mean-of-means aggregation.
        const getAverageFromLatestOutcomeRows = (columnIndex: number) => {
            if (latestOutcomeRows.length === 0) return 0
            const sum = latestOutcomeRows
                .map((row: any[]) => parseFloat(row[columnIndex]))
                .filter((value: number) => !Number.isNaN(value))
                .reduce((total: number, value: number) => total + value, 0)
            return sum / latestOutcomeRows.length
        }

        if (isMultiSelect) {
            publicInsurance = getAverageFromLatestOutcomeRows(outcomePublicIndex)
            privateInsurance = getAverageFromLatestOutcomeRows(outcomePrivateIndex)
        } else {
            const latestOutcomeRow = latestOutcomeRows[0]
            publicInsurance = latestOutcomeRow ? (parseFloat(latestOutcomeRow[outcomePublicIndex]) || 0) : 0
            privateInsurance = latestOutcomeRow ? (parseFloat(latestOutcomeRow[outcomePrivateIndex]) || 0) : 0
        }

        const raceColumns = outcomeRaceEthnicityData.response?.result?.column_order || []
        const getRaceColumnIndex = (name: string) => raceColumns.indexOf(name)
        const raceMonthIndex = getRaceColumnIndex('Month') !== -1 ? getRaceColumnIndex('Month') : 3
        const raceReportTypeIndex = getRaceColumnIndex('report_type') !== -1 ? getRaceColumnIndex('report_type') : 4
        const raceGroupIndex = getRaceColumnIndex('group') !== -1 ? getRaceColumnIndex('group') : 5
        const raceNumeratorIndex = getRaceColumnIndex('numerator') !== -1 ? getRaceColumnIndex('numerator') : 6
        const raceDenominatorIndex = getRaceColumnIndex('denominator') !== -1 ? getRaceColumnIndex('denominator') : 7
        const raceTypeIndex = getRaceColumnIndex('race_ethnicity_type') !== -1 ? getRaceColumnIndex('race_ethnicity_type') : 9

        if (!raceRows.some((row: any[]) => row[raceMonthIndex] !== undefined && row[raceGroupIndex] !== undefined)) {
            setOutcomeDemographicsSeries([])
            setOutcomeDemographicsCategories([])
            return
        }

        const exactMonthlyRaceRows = raceReportTypeIndex === -1
            ? raceRows
            : raceRows.filter((row: any[]) => String(row[raceReportTypeIndex] ?? '').trim() === 'Monthly')

        const normalizedMonthlyRaceRows = raceReportTypeIndex === -1
            ? raceRows
            : raceRows.filter((row: any[]) => String(row[raceReportTypeIndex] ?? '').trim().toLowerCase() === 'monthly')

        const monthlyCandidateRaceRows =
            exactMonthlyRaceRows.length > 0
                ? exactMonthlyRaceRows
                : normalizedMonthlyRaceRows.length > 0
                    ? normalizedMonthlyRaceRows
                    : raceRows
        let latestRaceRows = reportingMonthValue > 0
            ? monthlyCandidateRaceRows.filter((row: any[]) =>
                parseMonthToValue(String(row[raceMonthIndex] ?? '')) === reportingMonthValue)
            : []

        if (latestRaceRows.length === 0) {
            const latestRaceMonthValue = monthlyCandidateRaceRows.reduce((latestMonth: number, row: any[]) => {
                const currentMonth = parseMonthToValue(String(row[raceMonthIndex] ?? ''))
                return currentMonth > latestMonth ? currentMonth : latestMonth
            }, 0)
            latestRaceRows = latestRaceMonthValue > 0
                ? monthlyCandidateRaceRows.filter((row: any[]) =>
                    parseMonthToValue(String(row[raceMonthIndex] ?? '')) === latestRaceMonthValue)
                : monthlyCandidateRaceRows
        }

        // BIPOC and NH White come from the binary stratification:
        //   race_ethnicity_type 'binary' + group 'All Other Groups' = BIPOC, + 'NH White' = NH White.
        // Pool numerator/denominator (matches the Outcome Measures line chart and the
        // pooled-aggregation rule for multi-hospital selections — never mean-of-means).
        const pooledRatePercent = (matchGroup: (rawGroup: string) => boolean) => {
            let numSum = 0
            let denSum = 0
            let matched = false

            latestRaceRows.forEach((row: any[]) => {
                const raceType = String(row[raceTypeIndex] ?? '').trim().toLowerCase()
                if (raceType !== 'binary') return

                const rawGroup = String(row[raceGroupIndex] ?? '').trim()
                if (!matchGroup(rawGroup)) return

                const num = parseFloat(row[raceNumeratorIndex])
                const den = parseFloat(row[raceDenominatorIndex])
                if (Number.isNaN(num) || Number.isNaN(den)) return

                numSum += num
                denSum += den
                matched = true
            })

            if (!matched || denSum === 0) return 0
            return Math.round((numSum / denSum) * 100 * 100) / 100
        }

        const bipocValue = pooledRatePercent((rawGroup) => /^all\s*other\s*groups$/i.test(rawGroup))
        const nhWhiteValue = pooledRatePercent((rawGroup) =>
            /^nh\s*white$/i.test(rawGroup) || /^non[-\s]?hispanic\s*white$/i.test(rawGroup))

        setOutcomeDemographicsCategories([
            'BIPOC',
            'NH White',
            'Public Insurance',
            'Private Insurance',
        ])

        setOutcomeDemographicsSeries([
            {
                name: 'NTSV Cesarean Rate',
                data: [
                    bipocValue,
                    nhWhiteValue,
                    publicInsurance,
                    privateInsurance,
                ],
            },
        ])
    }, [outcomeInsuranceData, outcomeRaceEthnicityData, isMultiSelect, reportingMonthValue])

    useEffect(() => {
        if (!pmhClinicalData) {
            setPmhSeries([])
            setPmhCategories([])

            return
        }
        const rows = pmhClinicalData.response?.result?.rows || []
        if (rows.length === 0) {
            setPmhSeries([])
            setPmhCategories([])

            return
        }

        const columns = pmhClinicalData.response?.result?.column_order || []
        const getIdx = (name: string) => columns.indexOf(name)

        const timePeriodIdx = getIdx('month')
        const obProvidersIdx = getIdx('ob_providers')
        const obNursesIdx = getIdx('ob_nurses')
        const erProvidersIdx = getIdx('er_providers')
        const erNursesIdx = getIdx('er_nurses')

        const categories = ['Providers', 'Nurses']
        setPmhCategories(categories)

        if (isMultiSelect) {
            const hospitalIdIdx = getIdx('hospital_id')

            const nonBaselineMonthValues = new Set<number>()
            rows.forEach((row: any) => {
                const period = timePeriodIdx !== -1 ? String(row[timePeriodIdx] ?? '') : ''
                if (!period || period.toLowerCase().startsWith('baseline')) return
                const v = parseMonthToValue(period)
                if (v > 0) nonBaselineMonthValues.add(v)
            })
            const effectiveMonthValue = nonBaselineMonthValues.has(reportingMonthValue)
                ? reportingMonthValue
                : Math.max(0, ...Array.from(nonBaselineMonthValues))

            if (effectiveMonthValue === 0) {
                setPmhSeries([])
                return
            }

            const rowForHospAtCohort = new Map<string, any>()
            rows.forEach((row: any) => {
                const period = timePeriodIdx !== -1 ? String(row[timePeriodIdx] ?? '') : ''
                if (!period || period.toLowerCase().startsWith('baseline')) return
                if (parseMonthToValue(period) !== effectiveMonthValue) return
                const hid = hospitalIdIdx !== -1 ? String(row[hospitalIdIdx] ?? '').trim() : ''
                if (!hid) return
                rowForHospAtCohort.set(hid, row)
            })

            const obProv: number[] = []
            const obNur: number[] = []
            const erProv: number[] = []
            const erNur: number[] = []

            selectedHospitalIds.forEach((hidRaw) => {
                const hid = String(hidRaw ?? '').trim()
                const row = rowForHospAtCohort.get(hid)
                obProv.push(row && obProvidersIdx !== -1 ? (parseFloat(row[obProvidersIdx]) || 0) : 0)
                obNur.push(row && obNursesIdx !== -1 ? (parseFloat(row[obNursesIdx]) || 0) : 0)
                erProv.push(row && erProvidersIdx !== -1 ? (parseFloat(row[erProvidersIdx]) || 0) : 0)
                erNur.push(row && erNursesIdx !== -1 ? (parseFloat(row[erNursesIdx]) || 0) : 0)
            })

            const avg = (arr: number[]) => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 10) / 10 : 0

            if (rowForHospAtCohort.size === 0) {
                setPmhSeries([])
                return
            }

            setPmhSeries([
                { name: 'PMH OB Clinical Education', data: [avg(obProv), avg(obNur)] },
                { name: 'PMH ER Clinical Education', data: [avg(erProv), avg(erNur)] },
            ])
        } else {

            let matchingRow: any = reportingMonthValue > 0
                ? rows.find((row: any) => {
                    const period = timePeriodIdx !== -1 ? String(row[timePeriodIdx] ?? '') : ''
                    return period && !period.toLowerCase().startsWith('baseline')
                        && parseMonthToValue(period) === reportingMonthValue
                })
                : undefined

            if (!matchingRow) {
                let bestKey = -Infinity
                rows.forEach((row: any) => {
                    const period = timePeriodIdx !== -1 ? String(row[timePeriodIdx] ?? '') : ''
                    if (!period || period.toLowerCase().startsWith('baseline')) return
                    const k = parseMonthToValue(period)
                    if (k > bestKey) {
                        bestKey = k
                        matchingRow = row
                    }
                })
            }

            if (!matchingRow) {
                setPmhSeries([])
                return
            }

            setPmhSeries([
                {
                    name: 'PMH OB Clinical Education',
                    data: [
                        obProvidersIdx !== -1 ? (parseFloat(matchingRow[obProvidersIdx]) || 0) : 0,
                        obNursesIdx !== -1 ? (parseFloat(matchingRow[obNursesIdx]) || 0) : 0,
                    ]
                },
                {
                    name: 'PMH ER Clinical Education',
                    data: [
                        erProvidersIdx !== -1 ? (parseFloat(matchingRow[erProvidersIdx]) || 0) : 0,
                        erNursesIdx !== -1 ? (parseFloat(matchingRow[erNursesIdx]) || 0) : 0,
                    ]
                },
            ])
        }
    }, [pmhClinicalData, isMultiSelect, selectedHospitalIds, reportingMonthValue])

    const getReportingMonth = () => {
        const now = new Date()
        const m = now.getMonth()
        const y = now.getFullYear()
        const months = ['January','February','March','April','May','June','July','August','September','October','November','December']
        const prevM = m === 0 ? 11 : m - 1
        const prevY = m === 0 ? y - 1 : y
        return `${months[prevM]} ${prevY}`
    }

    const outcomeDemographicsValues = outcomeDemographicsSeries[0]?.data ?? []
    const raceCategories = outcomeDemographicsCategories.slice(0, 2)
    const insuranceCategories = outcomeDemographicsCategories.slice(2)
    const raceSeries = outcomeDemographicsSeries.length > 0
        ? [{ name: outcomeDemographicsSeries[0].name, data: outcomeDemographicsValues.slice(0, 2) }]
        : []
    const insuranceSeries = outcomeDemographicsSeries.length > 0
        ? [{ name: outcomeDemographicsSeries[0].name, data: outcomeDemographicsValues.slice(2) }]
        : []
    const outcomeLoading = outcomeInsuranceLoading || outcomeRaceEthnicityLoading
    const isInitialLoading = engagementInitialLoading || structureInitialLoading || educationInitialLoading || premInitialLoading || outcomeInsuranceInitialLoading || outcomeRaceEthnicityInitialLoading || pmhClinicalInitialLoading

    if (isInitialLoading) {
        return <LoadingNotice title="Loading status data..." />
    }

    return (
        <div className="bcbs-status-at-glance-page">
            <div id={generatePDFIds.dashboard()}>
                <div>
                    <h1 className="page-title">Status at a Glance</h1>
                    <p className="reporting-month">
                        Reporting Month: {getReportingMonth()}
                    </p>
                </div>

                {!isMultiSelect && (
                    <div id={generatePDFIds.chart(1)} className="chart-section" ref={engagementContainerRef}>
                        <div className="section-header-with-menu">
                            <h2 className="section-title">Engagement Measures</h2>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" 
                                onClick={() => {
                                    const menu = document.getElementById('export-menu-engagement');
                                    if (menu) menu.classList.toggle('show');
                                }}
                                >&#9776;</button>
                                <div id="export-menu-engagement" className="export-dropdown">
                                    <div onClick={() => { handleDownloadSVG(engagementContainerRef, 'Engagement Measures'); closeMenu('export-menu-engagement'); }}>Download SVG</div>
                                    <div onClick={() => { handleDownloadPNG(engagementContainerRef, 'Engagement Measures');closeMenu('export-menu-engagement'); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadEngagementCSV(); closeMenu('export-menu-engagement'); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <div className="engagement-container">
                            <div className="engagement-criteria-column">
                                <p className="criteria-status-subtitle">
                                    Criteria Status for {getReportingMonth()}
                                </p>
                                <div className="criteria-content">
                                    <div className="engagement-legend-horizontal">
                                        {engagementLabels.map((label, index) => (
                                            <div key={index} className="legend-item-horizontal">
                                                <div className="legend-text">
                                                    <strong>
                                                        <span
                                                            className="legend-dot"
                                                            style={{ backgroundColor: ['#429c75', '#c66736'][index] }}
                                                        ></span>
                                                        {label}:
                                                    </strong>
                                                    {Array.isArray(engagementMeasures[index]) && engagementMeasures[index].length > 0 && (
                                                        <div className="legend-measure">{engagementMeasures[index].join(', ')}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="engagement-chart-column">
                                <div className="criteria-status-above">Progress on Criteria Status</div>
                                <StackedBarChart
                                    ref={engagementBarChartRef}
                                    categories={engagementBarCategories}
                                    series={engagementBarSeries}
                                    colors={['#429c75', '#c66736']}
                                    height={210}
                                    yAxisMax={100}
                                    yAxisMin={0}
                                    yAxisTickAmount={5}
                                    exportFilename="Engagement_Progress_Criteria"
                                    isLoading={engagementLoading}
                                    columnWidth="60%"
                                    yAxisWidth={30}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {!isMultiSelect && (
                    <div id={generatePDFIds.chart(2)} className="chart-section" ref={structureContainerRef}>
                        <div className="section-header-with-menu">
                            <h2 className="section-title">Structure Measures</h2>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={() => {
                                    const menu = document.getElementById('export-menu-structure');
                                    if (menu) menu.classList.toggle('show');
                                }}>&#9776;</button>
                                <div id="export-menu-structure" className="export-dropdown">
                                    <div onClick={() => { handleDownloadSVG(structureContainerRef, 'Structure Measures'); closeMenu('export-menu-structure'); }}>Download SVG</div>
                                    <div onClick={() => { handleDownloadPNG(structureContainerRef, 'Structure Measures'); closeMenu('export-menu-structure'); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadStructureCSV(); closeMenu('export-menu-structure'); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                        <div className="structure-container">
                            <div className="structure-legend-horizontal">
                                {structureLabels.map((label, index) => (
                                    <div key={index} className="legend-item-horizontal">
                                        <div className="legend-text">
                                            <strong>
                                                <span
                                                    className="legend-dot"
                                                    style={{
                                                        backgroundColor: [
                                                            '#c66736',
                                                            '#F9E15B',
                                                            '#429c75',
                                                        ][index],
                                                    }}
                                                ></span>
                                                {label}:
                                            </strong>
                                            {structureMeasures[index] && (
                                                <div className="legend-measure">{structureMeasures[index]}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="structure-chart-column">
                                <DonutChart
                                    ref={structureMeasuresRef}
                                    labels={structureLabels}
                                    series={structureSeries}
                                    colors={['#c66736', '#F9E15B', '#429c75']}
                                    height={240}
                                    showLegend={false}
                                    type="pie"
                                    isLoading={structureLoading}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div id={generatePDFIds.chart(3)} className="chart-section" ref={educationContainerRef}>
                    <div className="section-header-with-menu">
                        <h2 className="section-title">Education Measures</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger" onClick={() => {
                                const menu = document.getElementById('export-menu-education');
                                if (menu) menu.classList.toggle('show');
                            }}>&#9776;</button>
                            <div id="export-menu-education" className="export-dropdown">
                                <div onClick={() => { handleDownloadSVG(educationContainerRef, 'BCBS_Education_Measures'); closeMenu('export-menu-education'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(educationContainerRef, 'BCBS_Education_Measures'); closeMenu('export-menu-education'); }}>Download PNG</div>
                                <div onClick={() => { educationChartRef.current?.exportToCSV(); closeMenu('export-menu-education'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>

                    <div className="education-criteria-table-wrapper">
                        <table className="education-criteria-table">
                            <thead>
                                <tr>
                                    <th className="col-criteria">Criteria Required</th>
                                    <th className="col-description"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="criteria-name">Educate Clinical Team</td>
                                    <td className="criteria-desc">
                                        <p>Provide clinical team education yearly and for new hires that addresses patient experience of care with a focus on reducing disparities: unconscious bias (implicit bias) training, respectful care, trauma - informed care, shared decision making, reducing stigma, and/or active listening.</p>
                                        <p>*Fulfills the Illinois state requirement to complete at least 1 hour of implicit bias awareness training per license renewal cycle.</p>
                                        <p>Must meet all measures; (*accommodated for PREM if average for the year was &gt;10%)</p>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="criteria-name">PMH Clinical Education</td>
                                    <td className="criteria-desc">
                                        <p>Achieve completion of PMH training by at least 80% of OB providers, OB nurses, ER providers, and ER nurses caring for pregnant/ postpartum patients</p>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p className="section-subtitle">
                        Education on the importance of listening to patients,
                        providing respectful care, and addressing implicit bias
                    </p>
                    <BarChart
                        ref={educationChartRef}
                        categories={educationCategories}
                        series={educationSeries}
                        colors={['#72b1df', '#3471ad']}
                        height={400}
                        goalLine={80}
                        goalLabel="Goal ≥ 80%"
                        plotOptionsBar={{ borderRadius: 0 }}
                        legendPosition="top"
                        legendAlign="center"
                        showLegendForSingleSeries={true}
                        xAxisLabel="Staff Type"
                        yAxisLabel="% of staff completing education"
                        exportFilename="Education Measures"
                        isLoading={educationLoading}
                        goalLabelOffsetX={0}
                        yAxisDecimals={0}
                        columnWidth="40"
                    />
                </div>

                <div id={generatePDFIds.chart(9)} className="chart-section" ref={pmhContainerRef}>
                    <div className="section-header-with-menu">
                        <h2 className="section-title">PMH Clinical Education</h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger" onClick={() => {
                                const menu = document.getElementById('export-menu-pmh')
                                if (menu) menu.classList.toggle('show')
                            }}>&#9776;</button>
                            <div id="export-menu-pmh" className="export-dropdown">
                                <div onClick={() => { handleDownloadSVG(pmhContainerRef, 'PMH_Clinical_Education'); closeMenu('export-menu-pmh') }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(pmhContainerRef, 'PMH_Clinical_Education'); closeMenu('export-menu-pmh') }}>Download PNG</div>
                                <div onClick={() => { pmhChartRef.current?.exportToCSV(); closeMenu('export-menu-pmh') }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <BarChart
                        ref={pmhChartRef}
                        categories={pmhCategories}
                        series={pmhSeries}
                        colors={['#72b1df', '#3471ad']}
                        height={400}
                        goalLine={80}
                        goalLabel="Goal ≥ 80%"
                        goalLabelOffsetX={0}
                        yAxisLabel="% of staff completing education"
                        xAxisLabel="Staff Type"
                        plotOptionsBar={{ borderRadius: 0 }}
                        legendPosition="top"
                        legendAlign="center"
                        yAxisMin={0}
                        yAxisMax={100}
                        yAxisTickAmount={5}
                        exportFilename="PMH Clinical Education"
                        isLoading={pmhClinicalLoading}
                        columnWidth="40"
                        showToolbar={false}
                    />
                </div>

                <div id={generatePDFIds.chart(8)} className="chart-section" ref={premContainerRef}>
                    <div className="section-header-with-menu">
                        <h2 className="section-title">
                            Patient Reported Experience Measure (PREM) Completion
                        </h2>
                        <div className="export-menu export-ignore">
                            <button className="hamburger" onClick={() => {
                                const menu = document.getElementById('export-menu-prem');
                                if (menu) menu.classList.toggle('show');
                            }}>&#9776;</button>
                            <div id="export-menu-prem" className="export-dropdown">
                                <div onClick={() => { handleDownloadSVG(premContainerRef, 'BCBS_PREM_Completion'); closeMenu('export-menu-prem'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(premContainerRef, 'BCBS_PREM_Completion'); closeMenu('export-menu-prem'); }}>Download PNG</div>
                                <div onClick={() => { premChartRef.current?.exportToCSV(); closeMenu('export-menu-prem'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <p className="section-subtitle">
                        Percentage of Total Deliveries with Patient Reported
                        Experience Measure (PREM) Completion
                    </p>
                    {premSeries?.length > 0 && premCategories?.length > 0 && (
                        <LineChart
                        ref={premChartRef}
                        categories={premCategories || []}
                        series={premSeries ||[]}
                        markers={false}
                        height={450}
                        goalLine={10}
                        goalLabel="Goal ≥ 10%"
                        min={0}
                        max={100}
                        tickAmount={20}
                        yAxisLabel="%Total Deliveries"
                        colors={["rgb(86,180,233)"]}
                        fontFamily="Helvetica, Arial, sans-serif"
                        showXAxisTooltip={false}
                        exportFilename="PREM Completion Status"
                        isLoading={premLoading}
                        showZeroForNull={true}
                        treatNullAsZero={true}
                        yAxisDecimals={1}
                        decimals={1}
                    />)}
                </div>

                <div  className="blue-card" ref={outcomeContainerRef}>
                    <h2 className="section-title white-text">Outcome Measures</h2>
                    <div className="outcome-content">
                        <div className="gauges-container">
                            <div id={generatePDFIds.chart(6)} className="gauge-wrapper" ref={gauge1ContainerRef}>
                                <div className="gauge-header">
                                    <div className="export-menu export-ignore">
                                        <button className="hamburger" onClick={() => {
                                            const menu = document.getElementById('export-menu-gauge1');
                                            if (menu) menu.classList.toggle('show');
                                        }}>&#9776;</button>
                                        <div id="export-menu-gauge1" className="export-dropdown">
                                            <div onClick={() => { handleDownloadSVG(gauge1ContainerRef, 'NTSV Cesarean Rate', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-gauge1'); }}>Download SVG</div>
                                            <div onClick={() => { handleDownloadPNG(gauge1ContainerRef, 'NTSV Cesarean Rate', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-gauge1'); }}>Download PNG</div>
                                            <div onClick={() => { handleDownloadGaugeCSV('NTSV Cesarean Rate', outcomeGauges.cesarean, 25); closeMenu('export-menu-gauge1'); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                <GaugeChart
                                    value={outcomeGauges.cesarean}
                                    label="Achieve NTSV Cesarean Rate at or Below 25%"
                                    goal={25}
                                    color="#72b1df"
                                    decimals={1}
                                    isLoading={outcomeLoading}
                                />
                            </div>
                            <div id={generatePDFIds.chart(7)} className="gauge-wrapper" ref={gauge2ContainerRef}>
                                <div className="gauge-header">
                                    <div className="export-menu export-ignore">
                                        <button className="hamburger" onClick={() => {
                                            const menu = document.getElementById('export-menu-gauge2');
                                            if (menu) menu.classList.toggle('show');
                                        }}>&#9776;</button>
                                        <div id="export-menu-gauge2" className="export-dropdown">
                                            <div onClick={() => { handleDownloadSVG(gauge2ContainerRef, 'SDOH Linkage', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-gauge2'); }}>Download SVG</div>
                                            <div onClick={() => { handleDownloadPNG(gauge2ContainerRef, 'SDOH Linkage', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-gauge2'); }}>Download PNG</div>
                                            <div onClick={() => { handleDownloadGaugeCSV('SDOH Linkage', outcomeGauges.sdoh, 75); closeMenu('export-menu-gauge2'); }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>
                                <GaugeChart
                                    value={outcomeGauges.sdoh}
                                    label="Link patients who screen positive for social determinant of health needs to needed resources"
                                    goal={75}
                                    color="#72b1df"
                                    decimals={1}
                                    isLoading={outcomeLoading}
                                />
                            </div>
                        </div>
                        <div id={generatePDFIds.chart(8)} className="demographics-chart" ref={demographicsContainerRef}>
                            <div className="demographics-header">
                                <h3 className="chart-title">
                                    Achieve at or below 25% NTSV cesarean rate for all
                                    stratified groups
                                </h3>
                                <div className="export-menu export-ignore">
                                    <button className="hamburger" onClick={() => {
                                        const menu = document.getElementById('export-menu-demographics');
                                        if (menu) menu.classList.toggle('show');
                                    }}>&#9776;</button>
                                    <div id="export-menu-demographics" className="export-dropdown">
                                        <div onClick={() => { handleDownloadSVG(demographicsContainerRef, 'NTSV Cesarean Rate by Demographics', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-demographics'); }}>Download SVG</div>
                                        <div onClick={() => { handleDownloadPNG(demographicsContainerRef, 'NTSV Cesarean Rate by Demographics', { sectionTitle: 'Outcome Measures' }); closeMenu('export-menu-demographics'); }}>Download PNG</div>
                                        <div onClick={() => { handleDownloadOutcomeDemographicsCSV(); closeMenu('export-menu-demographics'); }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            <div className="demographics-subsection">
                                <h4 className="demographics-subtitle">Race / Ethnicity</h4>
                                <BarChart
                                    categories={raceCategories}
                                    series={raceSeries}
                                    horizontal={true}
                                    colors={['#72b1df']}
                                    height={150}
                                    goalLine={25}
                                    goalLabel="Goal ≤ 25%"
                                    goalLabelPosition="top"
                                    goalLabelOrientation="horizontal"
                                    goalLabelOffsetX={36}
                                    goalLabelOffsetY={38}
                                    dataLabelsEnabled={true}
                                    dataLabelsOffsetX={11}
                                    categoryLabelsInside={true}
                                    yAxisShow={true}
                                    xAxisMin={0}
                                    xAxisMax={100}
                                    xAxisTickAmount={10}
                                    plotOptionsBar={{
                                        horizontal: true,
                                        barHeight: '55%',
                                        borderRadius: 0,
                                        dataLabels: {
                                            position: 'center'
                                        }
                                    }}
                                    dataLabelsFontWeight="bold"
                                    dataLabelsFontSize="14px"
                                    decimals={1}
                                    showLegend={false}
                                    tapTooltipOnTouch={true}
                                    exportFilename="Outcome Measures Demographics Race Ethnicity"
                                    isLoading={outcomeLoading}
                                />
                            </div>
                            <div className="demographics-subsection">
                                <h4 className="demographics-subtitle">Insurance</h4>
                                <BarChart
                                    categories={insuranceCategories}
                                    series={insuranceSeries}
                                    horizontal={true}
                                    colors={['#72b1df']}
                                    height={150}
                                    goalLine={25}
                                    goalLabel="Goal ≤ 25%"
                                    goalLabelPosition="top"
                                    goalLabelOrientation="horizontal"
                                    goalLabelOffsetX={36}
                                    goalLabelOffsetY={38}
                                    dataLabelsEnabled={true}
                                    dataLabelsOffsetX={11}
                                    categoryLabelsInside={true}
                                    yAxisShow={true}
                                    xAxisMin={0}
                                    xAxisMax={100}
                                    xAxisTickAmount={10}
                                    plotOptionsBar={{
                                        horizontal: true,
                                        barHeight: '55%',
                                        borderRadius: 0,
                                        dataLabels: {
                                            position: 'center'
                                        }
                                    }}
                                    dataLabelsFontWeight="bold"
                                    dataLabelsFontSize="14px"
                                    decimals={1}
                                    showLegend={false}
                                    tapTooltipOnTouch={true}
                                    exportFilename="Outcome Measures Demographics Insurance"
                                    isLoading={outcomeLoading}
                                />
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default StatusAtGlance